/**
 * AlertDetailsDialog — modal shown when the user clicks "Ver detalles" in the
 * notification drawer.
 *
 * Replaces the previous behavior of navigating directly to the linked page.
 * Now the user sees full alert context (formatted message + raw trigger_data
 * keys + status + timestamp) and chooses what to do next: acknowledge,
 * resolve, or follow the link.
 *
 * Receives the same `alert` shape that the drawer builds in `mergedItems`.
 * Defensive about field names since both snake_case (from /alerts) and
 * camelCase (from the WebSocket push) flow through here.
 */
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Chip,
  Typography,
  Divider,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Close,
  OpenInNew,
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircleOutlined,
  VisibilityRounded,
  TaskAltRounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatAlertTitle, formatAlertMessage } from '@/utils/alertFormatter';

export interface AlertDialogPayload {
  alertId?: number;
  alertType?: string;
  alert_type?: string;
  title?: string;
  message?: string;
  severity?: 'critical' | 'warning' | 'info' | string;
  status?: 'active' | 'acknowledged' | 'resolved' | string;
  timestamp?: string;
  triggered_at?: string;
  farmName?: string;
  link?: string;
  trigger_data?: Record<string, any> | null;
  triggerData?: Record<string, any> | null;
  sourceType?: string;
  sourceId?: string;
}

interface Props {
  alert: AlertDialogPayload | null;
  open: boolean;
  onClose: () => void;
  onAcknowledge?: (alertId: number) => void;
  onResolve?: (alertId: number) => void;
  /** Called when the user clicks "Go to page" — typically closes the drawer. */
  onNavigate?: () => void;
}

const SEVERITY_ICON: Record<string, React.ReactNode> = {
  critical: <ErrorIcon color="error" />,
  warning: <Warning color="warning" />,
  info: <Info color="info" />,
};

const SEVERITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  critical: 'error',
  warning: 'warning',
  info: 'info',
};

/** Keys that are already rendered elsewhere in the dialog (farm/title/link)
 *  — skip them in the raw "Details" section to avoid duplication. */
const HIDDEN_KEYS = new Set([
  'farm_id', 'farm_name', 'farmId', 'farmName',
  'link', 'title', 'message',
]);

/** Format a snake_case or camelCase key as a friendly label.
 *  e.g. `silent_seconds` → "Silent seconds", `lastSeen` → "Last seen". */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Best-effort scalar render — pretty for ISO timestamps, percentages, booleans. */
function formatValue(key: string, value: unknown, lang: string): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  if (typeof value === 'number') {
    if (key.toLowerCase().includes('percent') && Math.abs(value) <= 1) {
      return `${(value * 100).toFixed(0)}%`;
    }
    if (key.toLowerCase().includes('percent')) return `${Math.round(value)}%`;
    return String(value);
  }
  if (typeof value === 'string') {
    // ISO-ish timestamp?
    if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
      try {
        return new Date(value).toLocaleString(lang);
      } catch {
        return value;
      }
    }
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

const AlertDetailsDialog: React.FC<Props> = ({
  alert,
  open,
  onClose,
  onAcknowledge,
  onResolve,
  onNavigate,
}) => {
  const { t, i18n } = useTranslation(['alerts', 'common']);
  const navigate = useNavigate();

  if (!alert) return null;

  const alertType = alert.alertType ?? alert.alert_type;
  const triggerData = alert.trigger_data ?? alert.triggerData ?? {};
  const title = formatAlertTitle(t, alertType, alert.title ?? '', triggerData);
  const message = formatAlertMessage(t, i18n.language, alert);
  const severity = alert.severity ?? 'info';
  const triggeredAt = alert.timestamp ?? alert.triggered_at;
  const triggeredAtStr = triggeredAt
    ? new Date(triggeredAt).toLocaleString(i18n.language)
    : null;

  const status = alert.status ?? 'active';
  const isActive = status === 'active';
  const isResolved = status === 'resolved';

  const detailEntries = Object.entries(triggerData)
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== null && v !== undefined && v !== '')
    .sort(([a], [b]) => a.localeCompare(b));

  const handleAck = () => {
    if (alert.alertId && onAcknowledge) onAcknowledge(alert.alertId);
  };
  const handleResolve = () => {
    if (alert.alertId && onResolve) onResolve(alert.alertId);
  };
  const handleNavigate = () => {
    if (!alert.link) return;
    onNavigate?.();
    onClose();
    navigate(alert.link);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {SEVERITY_ICON[severity] ?? SEVERITY_ICON.info}
          <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          size="small"
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Chips row */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={t(`severity.${severity}`, { defaultValue: severity })}
              color={SEVERITY_COLOR[severity] ?? 'default'}
            />
            {status && (
              <Chip
                size="small"
                variant="outlined"
                label={t(`status.${status}`, { defaultValue: status })}
                color={isResolved ? 'success' : isActive ? 'warning' : 'default'}
              />
            )}
            {alert.farmName && (
              <Chip size="small" variant="outlined" label={alert.farmName} />
            )}
            {triggeredAtStr && (
              <Chip
                size="small"
                variant="outlined"
                label={triggeredAtStr}
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Message */}
          <Typography variant="body1">{message}</Typography>

          {/* Raw trigger_data details */}
          {detailEntries.length > 0 && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                {t('alerts:details.heading', 'Details')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 2fr',
                  rowGap: 0.75,
                  columnGap: 2,
                }}
              >
                {detailEntries.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ alignSelf: 'center' }}
                    >
                      {humanizeKey(k)}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-word',
                        fontFamily: typeof triggerData[k] === 'string'
                          && /^[0-9a-fA-F]{16}$/.test(triggerData[k] as string)
                          ? 'monospace'
                          : undefined,
                      }}
                    >
                      {formatValue(k, v, i18n.language)}
                    </Typography>
                  </React.Fragment>
                ))}
              </Box>
            </>
          )}

          {/* Source */}
          {(alert.sourceType || alert.sourceId) && (
            <>
              <Divider />
              <Typography variant="caption" color="text.disabled">
                {t('alerts:details.source', 'Source')}: {alert.sourceType} {alert.sourceId}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {alert.alertId && isActive && onAcknowledge && (
          <Button
            size="small"
            startIcon={<VisibilityRounded />}
            onClick={handleAck}
          >
            {t('alerts:actions.acknowledge', 'Acknowledge')}
          </Button>
        )}
        {alert.alertId && !isResolved && onResolve && (
          <Button
            size="small"
            color="success"
            startIcon={<TaskAltRounded />}
            onClick={handleResolve}
          >
            {t('alerts:actions.resolve', 'Resolve')}
          </Button>
        )}
        {alert.link && (
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNew />}
            onClick={handleNavigate}
          >
            {t('alerts:actions.goToPage', 'Go to page')}
          </Button>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} startIcon={<CheckCircleOutlined />}>
          {t('common:actions.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlertDetailsDialog;
